import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ContactDetailsStepProps {
  data: {
    fullName: string;
    email: string;
    phone: string;
  };
  onUpdate: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const ContactDetailsStep = ({ data, onUpdate, onNext, onBack }: ContactDetailsStepProps) => {
  const [formData, setFormData] = useState(data);
  const { toast } = useToast();

  const handleChange = (field: string, value: string) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    onUpdate(updated);
  };

  const handleNext = () => {
    if (!formData.fullName || !formData.email || !formData.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in all contact details to continue.",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    onNext();
  };

  return (
    <>
      <CardHeader>
        <CardTitle className="text-2xl">How can the employer contact you?</CardTitle>
        <p className="text-muted-foreground">
          We'll only share this information with the employer for this application.
        </p>
      </CardHeader>

      <CardContent className="space-y-6 pb-8">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName">Full Name *</Label>
            <Input
              id="fullName"
              placeholder="Enter your full name"
              value={formData.fullName}
              onChange={(e) => handleChange("fullName", e.target.value)}
              className="h-12 text-lg"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="h-12 text-lg"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number *</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="07XXX XXXXXX"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              className="h-12 text-lg"
            />
          </div>
        </div>

        <div className="bg-muted/50 p-4 rounded-lg flex items-start gap-3">
          <Shield className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">Your privacy matters.</span> Your information 
            will only be shared with this employer for this job application.
          </p>
        </div>

        <div className="flex gap-3 pt-4">
          <Button variant="outline" onClick={onBack} className="flex-1">
            Back
          </Button>
          <Button onClick={handleNext} className="flex-1">
            Next: Add Your Experience
          </Button>
        </div>

        <div className="text-center">
          <button className="text-sm text-muted-foreground hover:text-foreground underline">
            Save and finish later
          </button>
        </div>
      </CardContent>
    </>
  );
};
